import axios from 'axios';

import {
    API_BASE_URL,
    KYBAvailableServices
} from '../../constants/index';

export default class SupplierOverviewService {
	static instance;

	static getInstance() {
		if (!SupplierOverviewService.instance) {
			SupplierOverviewService.instance = new SupplierOverviewService();
		}

		return SupplierOverviewService.instance;
	}

    async fetchStatistics(supplierId, params = {}){
        const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/statistics`, {
            params  :   params,
            headers :   {
                Authorization: `Bearer ${localStorage.getItem(
                    "LOCAL_STORAGE_KEY_ACCESS_TOKEN"
                )}`
            },
        });
        return response.data?.data || {};
    }   

    async fetchLastActivities(supplierId, params = {}){
        const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/last_activities`, {
            params  :   params,
            headers :   {
                Authorization: `Bearer ${localStorage.getItem(
                    "LOCAL_STORAGE_KEY_ACCESS_TOKEN"
                )}`
            },
        });
        return response.data?.data || [];
    }   

    async fetchLatestFindings(supplierId, params = {}){
        const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/last_findings`, {
            params  :   params,
            headers :   {
                Authorization: `Bearer ${localStorage.getItem(
                    "LOCAL_STORAGE_KEY_ACCESS_TOKEN"
                )}`
            },
        });
        return response.data?.data || [];
    }

    async fetchAiSummary(supplierId, refresh = 0, params = {}) {
        if (supplierId === 7436) {
            return {
                "header": {
                    "reportType": "PPWR, EUDR",
                    "thirdPartyName": "Barry Callebaut AG",
                    "overallRiskScore": {
                        "title": "High Risk",
                        "color": "FF4949"
                    },
                    "createdOn": "2026-07-25 10:15:00"
                },
                "summary": {
                    "paragraphs": [
                        "Barry Callebaut AG, headquartered in Zurich, Switzerland, is a global cocoa and chocolate manufacturer sourcing from Côte d’Ivoire, Ghana, Cameroon, and Brazil. Core KYB records, including corporate registration, beneficial ownership information, the company profile, and the due diligence report, are available. The company has established sustainability and supplier-monitoring programs covering 26 suppliers and 24 EUDR-relevant cocoa and chocolate product groups; however, EUDR geolocation evidence remains pending for selected sourcing areas.",
                        
                        "The EUDR assessment identified incomplete farm-plot geolocation and supporting origin or traceability evidence for selected suppliers in Ghana and Cameroon. Of 48 monitored trade operations, 42 have a submitted Due Diligence Statement, but only 36 have been verified by TRACE; six submitted statements therefore still require validation, while six upcoming cocoa shipments have no completed DDS record. Three suppliers remain in the high or very high risk tiers because their cocoa sourcing exposure requires stronger geolocation validation, deforestation due diligence evidence, and documented origin checks.",
                        
                        "The High Risk rating reflects the combined effect of these outstanding EUDR controls and six open PPWR review issues, rather than evidence of major misconduct or broad non-compliance. Across 32 packaging items and 40 uploaded documents, 34 documents have been processed, but evidence gaps remain for mixed-material packaging recyclability, plastic and aluminium recycled-content declarations, and supporting packaging material documentation. Risk can be reduced by validating the remaining farm polygons and origin records, completing the six missing DDS files, obtaining TRACE verification for the six submitted statements still under review, collecting the outstanding PPWR declarations, completing design-for-recycling assessments, and maintaining continuous supplier and deforestation monitoring."
                    ],
                    "fromCache": true
                },
                "riskAnalysis": {
                    "rating": "high",
                    "title": "High Risk",
                    "color": "#FF4949",
                    "fromCache": true
                },
                "verification": {
                    "items": [
                        {
                            "key": "registration_vat",
                            "label": "Registration and VAT ID",
                            "status": "available"
                        },
                        {
                            "key": "kyb_report",
                            "label": "Due Diligence/KYB Report",
                            "status": "available"
                        },
                        {
                            "key": "eudr_geodata",
                            "label": "EUDR Geodata",
                            "status": "pending"
                        },
                        {
                            "key": "company_profile_report",
                            "label": "Company profile report",
                            "status": "pulled_from_api"
                        },
                        {
                            "key": "ubo_data",
                            "label": "UBO data",
                            "status": "available"
                        }
                    ]
                },
                "lastUpdated": "2026-07-25 10:16:30"
            }
        }
        
        const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/ai-summary`, {
            params: {
                ...params,
                refresh,
            },
            headers: {
                Authorization: `Bearer ${localStorage.getItem('LOCAL_STORAGE_KEY_ACCESS_TOKEN')}`,
            },
        });

        return response.data?.data || {};
    }

    async fetchStatisticsWidgets(supplierId, params = {}) {
        if (supplierId === 7436) {
            return {
                widgets: [
                    {
                        key: 'ppwr_compliance_status',
                        value: 'Medium Risk'
                    },
                    {
                        key: 'supplier_documents',
                        value: 40
                    },
                    {
                        key: 'packaging_items',
                        value: 32
                    },
                    {
                        key: 'last_submission',
                        value: 'July 25, 2026'
                    },
                    {
                        key: 'open_issues',
                        value: 6
                    }
                ]
            }
        }

        const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/general-statistics`, {
            params,
            headers: {
                Authorization: `Bearer ${localStorage.getItem('LOCAL_STORAGE_KEY_ACCESS_TOKEN')}`,
            },
        });

        const data = response.data?.data || {};

        return {
            widgets: [
                { key: 'all_risks_found', value: data.allRisksFound, feature_flag: 'kyb_activation' },
                { key: 'esg_databases', value: data.esgDatabases, feature_flag: 'kyb_activation' },
                { key: 'sanctions', value: data.sanctions, feature_flag: 'kyb_activation' },
                { key: 'pep', value: data.pep, feature_flag: 'kyb_activation' },
                { key: 'adverse_news', value: data.adverseNews, feature_flag: 'kyb_activation' },
                { key: 'regulatory_enforcement', value: data.regulatoryEnforcement, feature_flag: 'kyb_activation' },
                { key: 'insolvency_records', value: data.insolvencyRecords, feature_flag: 'kyb_activation' },
                { key: 'reputational_risk_exposure', value: data.reputationalRiskExposure, feature_flag: 'kyb_activation' },
                { key: 'deforestation_risk', value: data.deforestationRisk, feature_flag: 'eudr_activation' }
            ],
        };
    }

    async fetchSearchOverview(supplierId, months = 6, params = {}) {
        const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/search-overview`, {
            params: {
                ...params,
                months,
            },
            headers: {
                Authorization: `Bearer ${localStorage.getItem('LOCAL_STORAGE_KEY_ACCESS_TOKEN')}`,
            },
        });

        const data = response.data?.data || {};

        return {
            kpis: {
                totalSearchItems: data.totalSearchItems,
                monitoredItems: data.monitoredItems,
                screenedItems: data.screenedItems,
            },
            monthlyTrends: data.months || []
        };
    }

    async fetchCorporateStructureComparison(supplierId, params = {}) {
        const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/shareholder-graph`, {
            params,
            headers: {
                Authorization: `Bearer ${localStorage.getItem('LOCAL_STORAGE_KEY_ACCESS_TOKEN')}`,
            },
        });

        const data = response.data?.data || {};

        if (!data.source) {
            return {
                source: null,
                target: null,
            };
        }

        const source = KYBAvailableServices.find((service) => service.id === data.source);

        return {
            source: {
                providerName: source?.title || data.source,
                shareholderGraph: data.graph || null,
            },
            target: null,
        };
    }

    async fetchCrossCheckAnalysis(supplierId, refresh = 0, params = {}) {
        const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/cross-check-analysis`, {
            params: {
                ...params,
                refresh,
            },
            headers: {
                Authorization: `Bearer ${localStorage.getItem('LOCAL_STORAGE_KEY_ACCESS_TOKEN')}`,
            },
        });

        const data = response.data?.data || {};
        const rows = data.rows || {};

        const reportSources = [
            { key: 'companyProfileReport', label: 'Company Profile Report' },
            { key: 'transparencyRegister', label: 'Transparency Register' },
        ];

        const criteria = Object.entries(rows).map(([key, row]) => ({
            key,
            label: row.label,
            values: {
                companyProfileReport: row.companyProfileReport,
                transparencyRegister: row.transparencyRegister,
            },
        }));

        return {
            reportSources,
            criteria,
        };
    }

    async fetchTaskRiskOverview(supplierId, params = {}) {
        const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/task-risk-overview`, {
            params,
            headers: {
                Authorization: `Bearer ${localStorage.getItem('LOCAL_STORAGE_KEY_ACCESS_TOKEN')}`,
            },
        });

        return response.data?.data || {};
    }

    async fetchNextSteps(supplierId, params = {}) {
        // Replace mock implementation with real API call when backend is available.
        // const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/next_steps`, {
        //     params,
        //     headers: {
        //         Authorization: `Bearer ${localStorage.getItem('LOCAL_STORAGE_KEY_ACCESS_TOKEN')}`,
        //     },
        // });
        // return response.data?.data || {};

        await new Promise((resolve) => setTimeout(resolve, 350));

        return {
            supplierId,
            assigneeOptions: [
                { value: '1', label: 'Sarah Chen' },
                { value: '2', label: 'James Mitchell' },
                { value: '3', label: 'Elena Rodriguez' },
                { value: '4', label: 'Michael Bauer' },
            ],
            companies: [
                {
                    id: 'company-barry-callebaut',
                    name: 'Barry Callebaut AG',
                    type: 'Supplier',
                    country: 'Switzerland',
                    riskLevel: 'high',
                    taskCount: 3,
                    items: [
                        {
                            id: 'rec-1',
                            title: 'Complete DDS records for upcoming cocoa shipments',
                            findingSummary: 'Six planned cocoa shipments still require complete Due Diligence Statements before dispatch.',
                            riskLevel: 'high',
                            recommendedRisk: 'medium',
                            recommendedTasks: 'Collect origin evidence, Complete and submit DDS records',
                            isAiRecommendation: true,
                            isAiGenerated: true,
                            aiTooltip: 'AI identified six upcoming shipments without complete DDS records and recommends resolving them before dispatch.',
                            assignedTo: null,
                            deadline: null,
                        },
                        {
                            id: 'rec-2',
                            title: 'Validate outstanding cocoa farm geolocation evidence',
                            findingSummary: 'Geolocation evidence for selected supplier plots in Ghana and Cameroon remains under review.',
                            riskLevel: 'high',
                            recommendedRisk: 'high',
                            recommendedTasks: 'Validate polygon data, Record analyst decision',
                            isAiRecommendation: true,
                            isAiGenerated: true,
                            aiTooltip: 'AI recommends validating the remaining farm polygons because they affect EUDR risk classification and DDS completion.',
                            assignedTo: { value: '2', label: 'James Mitchell' },
                            deadline: '2026-08-20',
                        },
                        {
                            id: 'task-1',
                            title: 'Review Côte d’Ivoire source-plot monitoring results',
                            findingSummary: 'Recent monitoring results for higher-risk cocoa plots require sustainability team review and sign-off.',
                            riskLevel: 'high',
                            risk: 'high',
                            task: 'Review Côte d’Ivoire source-plot monitoring results',
                            isAiRecommendation: false,
                            isAiGenerated: false,
                            assignedTo: { value: '1', label: 'Sarah Chen' },
                            deadline: '2026-08-05',
                            status: 'IN_PROGRESS',
                        },
                    ],
                },
                {
                    id: 'company-packaging-europe',
                    name: 'Barry Callebaut Packaging Operations',
                    type: 'Business Unit',
                    country: 'Belgium',
                    riskLevel: 'medium',
                    taskCount: 2,
                    items: [
                        {
                            id: 'rec-3',
                            title: 'Obtain recycled-content declarations',
                            findingSummary: 'Current declarations do not fully confirm recycled content for selected plastic and aluminium packaging.',
                            riskLevel: 'medium',
                            recommendedRisk: 'medium',
                            recommendedTasks: 'Request supplier declarations, Validate recycled-content percentages',
                            isAiRecommendation: true,
                            isAiGenerated: true,
                            aiTooltip: 'AI recommends collecting updated supplier declarations to close PPWR recycled-content evidence gaps.',
                            assignedTo: null,
                            deadline: null,
                        },
                        {
                            id: 'task-3',
                            title: 'Complete mixed-material packaging recyclability review',
                            findingSummary: 'The chocolate box liner and selected wrappers require final design-for-recycling assessment.',
                            riskLevel: 'medium',
                            risk: 'medium',
                            task: 'Complete mixed-material packaging recyclability review',
                            isAiRecommendation: false,
                            isAiGenerated: false,
                            assignedTo: { value: '4', label: 'Michael Bauer' },
                            deadline: '2026-08-15',
                            status: 'REVIEW',
                        },
                    ],
                },
                {
                    id: 'company-cocoa-sourcing-west-africa',
                    name: 'Barry Callebaut Cocoa Sourcing West Africa',
                    type: 'Sourcing Unit',
                    country: 'Côte d’Ivoire',
                    riskLevel: 'high',
                    taskCount: 1,
                    items: [
                        {
                            id: 'task-4',
                            title: 'Refresh deforestation hotspot monitoring',
                            findingSummary: 'Quarterly monitoring is due for supplier plots in the Western Côte d’Ivoire cocoa belt.',
                            riskLevel: 'high',
                            risk: 'high',
                            task: 'Refresh deforestation hotspot monitoring',
                            isAiRecommendation: false,
                            isAiGenerated: false,
                            assignedTo: { value: '2', label: 'James Mitchell' },
                            deadline: '2026-09-10',
                            status: 'IN_PROGRESS',
                        },
                    ],
                },
            ],
        };
    }

    async fetchEudrComplianceCheckData(supplierId, params = {}) {
        // Replace mock implementation with real API call when backend is available.
        // const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/eudr_compliance_check`, {
        //     params,
        //     headers: {
        //         Authorization: `Bearer ${localStorage.getItem('LOCAL_STORAGE_KEY_ACCESS_TOKEN')}`,
        //     },
        // });
        // return response.data?.data || {};

        await new Promise((resolve) => setTimeout(resolve, 350));

        return {
            supplierId,
            checks: [
                {
                    id: '1',
                    requirement: 'Supplier farm-plot geolocation evidence',
                    articles: [
                        'Art. 9',
                        'Art. 10'
                    ],
                    redFlag: true,
                    evidenceSource: {
                        title: 'Ghana and Cameroon Plot Geodata',
                        link: 'https://demo.diss-co.com/evidence/eudr/plot-geodata'
                    },
                    actionNeeded: 'Validate evidence',
                    taskStatus: 'NEW'
                },
                {
                    id: '2',
                    requirement: 'DDS completeness for upcoming cocoa shipments',
                    articles: [
                        'Art. 4',
                        'Art. 9'
                    ],
                    redFlag: true,
                    evidenceSource: {
                        title: 'Shipment DDS Register',
                        link: 'https://demo.diss-co.com/evidence/eudr/dds-register'
                    },
                    actionNeeded: 'Complete 6 DDS records',
                    taskStatus: 'IN_PROGRESS'
                },
                {
                    id: '3',
                    requirement: 'Deforestation risk assessment and monitoring',
                    articles: [
                        'Art. 10',
                        'Art. 11'
                    ],
                    redFlag: false,
                    evidenceSource: {
                        title: 'Q2 2026 Satellite Monitoring Report',
                        link: 'https://demo.diss-co.com/evidence/eudr/satellite-monitoring'
                    },
                    actionNeeded: 'Continue monitoring',
                    taskStatus: 'REVIEW'
                },
            ]
        };
    }

    async fetchTopPackagingProducts(supplierId, params = {}) {
        // Replace mock implementation with real API call when backend is available.
        // const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/top-packaging-products`, {
        //     params,
        //     headers: {
        //         Authorization: `Bearer ${localStorage.getItem('LOCAL_STORAGE_KEY_ACCESS_TOKEN')}`,
        //     },
        // });
        // return response.data?.data || {};

        await new Promise((resolve) => setTimeout(resolve, 350));

        return {
            supplierId,
            products: [
                {
                    id: 'pkg-1',
                    artNumber: 'BC-PKG-1001',
                    product: 'Chocolate Packaging Box',
                    material: 'FSC Paperboard + PE Inner Liner',
                    redFlag: true,
                    recommendedRisk: 'Mixed-material liner requires final recyclability assessment',
                    recommendedRiskLevel: 'high',
                    recommendedTask: 'Complete design-for-recycling review',
                },
                {
                    id: 'pkg-2',
                    artNumber: 'BC-PKG-1002',
                    product: 'Cocoa Powder Bag',
                    material: 'Multi-layer PE Bag',
                    redFlag: true,
                    recommendedRisk: 'Recycled-content declaration is incomplete',
                    recommendedRiskLevel: 'medium',
                    recommendedTask: 'Request recycled content declaration from packaging supplier',
                },
                {
                    id: 'pkg-3',
                    artNumber: 'BC-PKG-1003',
                    product: 'Cocoa Bean Sack',
                    material: 'Reinforced Paper Sack',
                    redFlag: false,
                    recommendedRisk: 'Fibre sourcing and material declaration verified',
                    recommendedRiskLevel: 'low',
                    recommendedTask: 'Maintain annual supplier declaration',
                },
                {
                    id: 'pkg-4',
                    artNumber: 'BC-PKG-1004',
                    product: 'Chocolate Wrapper',
                    material: 'Aluminium + Paper Laminate',
                    redFlag: true,
                    recommendedRisk: 'Recyclability and recycled aluminium content require confirmation',
                    recommendedRiskLevel: 'medium',
                    recommendedTask: 'Validate recyclability and recycled-content declarations',
                },
                {
                    id: 'pkg-5',
                    artNumber: 'BC-PKG-1005',
                    product: 'Wooden Pallet',
                    material: 'ISPM 15 Wood + PE Stretch Film',
                    redFlag: false,
                    recommendedRisk: 'Wood sourcing evidence is current; stretch-film use remains monitored',
                    recommendedRiskLevel: 'low',
                    recommendedTask: 'Maintain certification and film-use records',
                },
            ]
        };
    }

    async createPackagingProductRiskAndTask(supplierId, productId, params = {}) {
        // Replace mock implementation with real API call when backend is available.
        // const response = await axios.post(
        //     `${API_BASE_URL}/supplier/${supplierId}/overview/top-packaging-products/${productId}/create-risk-task`,
        //     params,
        //     {
        //         headers: {
        //             Authorization: `Bearer ${localStorage.getItem('LOCAL_STORAGE_KEY_ACCESS_TOKEN')}`,
        //         },
        //     }
        // );
        // return response.data?.data || {};

        await new Promise((resolve) => setTimeout(resolve, 500));

        return {
            supplierId,
            productId,
            success: true,
        };
    }

    async fetchPackagingMaterialOverview(supplierId, params = {}) {
        // Replace mock implementation with real API call when backend is available.
        // const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/packaging-material-overview`, {
        //     params,
        //     headers: {
        //         Authorization: `Bearer ${localStorage.getItem('LOCAL_STORAGE_KEY_ACCESS_TOKEN')}`,
        //     },
        // });
        // return response.data?.data || {};

        await new Promise((resolve) => setTimeout(resolve, 350));

        return {
            supplierId,
            total:32,
            items:[
                {
                    key:'paper_cardboard',
                    title:'Paper & Cardboard',
                    amount:14,
                    percent:44,
                    color:'#1CBB8C',
                },
                {
                    key:'plastic',
                    title:'Plastic Wrappers',
                    amount:8,
                    percent:25,
                    color:'#5664D2',
                },
                {
                    key:'aluminium',
                    title:'Aluminium Foil',
                    amount:6,
                    percent:19,
                    color:'#F2994A',
                },
                {
                    key:'wood',
                    title:'Wooden Pallets',
                    amount:4,
                    percent:12,
                    color:'#EB5757',
                },
                {
                    key:'other',
                    title:'Other',
                    amount:0,
                    percent:0,
                    color:'#828282',
                },
            ]
        };
    }

    async fetchEsgRating(supplierId, params = {}) {
        if (Number(supplierId) === 7286) {
            await new Promise((resolve) => setTimeout(resolve, 350));

            return {
                supplierId,
                companyName: 'HUGO BOSS',
                aiSummary: 'HUGO BOSS shows strong ESG performance across major rating providers and ranks among the stronger performers in its peer group. The company reports low ESG risk and targets a 50% reduction in Scope 1–3 emissions by 2030, with net-zero emissions targeted by 2050.',
                ratingsFound: {
                    count: 5,
                    sources: [
                        { key: 'msci', name: 'MSCI', value: 'AA' },
                        { key: 'sustainalytics', name: 'Sustainalytics', value: '14.2 · Low Risk' },
                        { key: 'sp_global', name: 'S&P Global', value: '84 / 85' },
                        { key: 'iss_esg', name: 'ISS ESG', value: 'C+ · Prime' },
                        { key: 'cdp', name: 'CDP', value: 'B / B−' },
                    ],
                },
                ratingsBySource: [
                    { source: 'MSCI ESG', rating: 'AA', assessment: 'Leader', assessmentColor: '#414FBD' },
                    { source: 'Sustainalytics', rating: '14.2', assessment: 'Low Risk', assessmentColor: '#59C94F' },
                    { source: 'S&P Global', rating: '84 / 85', assessment: 'Strong ESG Performance', assessmentColor: '#59C94F' },
                    { source: 'ISS ESG', rating: 'C+', assessment: 'Prime', assessmentColor: '#414FBD' },
                    { source: 'CDP', rating: 'B / B−', assessment: 'Climate: B · Water: B−', assessmentColor: '#FFA049' },
                ],
                peerComparison: [
                    {
                        key: 'sp_global',
                        provider: 'S&P Global (0–100)',
                        hint: "Measures a company's sustainability performance across financially material ESG factors.\nScale: 0–100\nHigher is better.",
                        hugoBoss: '84 / 85',
                        industryAvg: '~32',
                        standing: 'Top Tier',
                        standingColor: '#59C94F',
                        peers: 'Moncler ~89 · Burberry ~80 · Kering ~82',
                    },
                    {
                        key: 'msci',
                        provider: 'MSCI ESG (CCC–AAA)',
                        hint: "Measures a company's exposure to financially relevant ESG risks and how well it manages those risks.\nScale: CCC–AAA\nHigher is better.",
                        hugoBoss: 'AA',
                        industryAvg: 'BBB',
                        standing: 'Leader',
                        standingColor: '#414FBD',
                        peers: 'Moncler AA · Kering AAA · Ralph Lauren A',
                    },
                    {
                        key: 'sustainalytics',
                        provider: 'Sustainalytics (0–100)',
                        hint: "Measures a company's exposure to material ESG risks and how well those risks are managed.\nScale: 0–100\nLower is better.",
                        hugoBoss: '14.2',
                        industryAvg: '~22.5',
                        standing: 'Low Risk',
                        standingColor: '#59C94F',
                        peers: 'Burberry 20.1 · Ralph Lauren 24.3 · Capri 31.8',
                    },
                    {
                        key: 'iss_esg',
                        provider: 'ISS ESG',
                        hint: "Evaluates a company's ESG performance across environmental, social and governance factors.\nScale: A+ to D−\nHigher is better.",
                        hugoBoss: 'C+',
                        industryAvg: 'C−',
                        standing: 'Prime',
                        standingColor: '#414FBD',
                        peers: 'Moncler B− · Kering B · Burberry C+',
                    },
                ],
                report: {
                    title: 'HUGO BOSS Annual Report / Sustainability Report 2025',
                    meta: 'Publication year: 2025 · Source: hugoboss.com',
                    url: 'https://group.hugoboss.com/en/sustainability',
                    carbonEmissions: [
                        { key: 'scope1', label: 'Scope 1', value: '9,152', unit: 'tCO₂e', hint: 'Direct emissions' },
                        { key: 'scope2', label: 'Scope 2', value: '11,442', unit: 'tCO₂e', hint: 'Market-based' },
                        { key: 'scope3', label: 'Scope 3', value: '642,946', unit: 'tCO₂e', hint: 'Value chain' },
                    ],
                    targets: [
                        {
                            key: '2030',
                            label: '2030 Target',
                            value: '−50%',
                            highlight: 'Scope 1–3 emissions',
                            hint: 'vs. 2024 baseline',
                            emphasized: true,
                        },
                        {
                            key: 'net_zero',
                            label: 'Net Zero Target',
                            value: '2050',
                            highlight: null,
                            hint: 'Net zero emissions',
                            emphasized: false,
                        },
                    ],
                    keyFigures: [
                        { key: 'renewable', label: 'Renewable Electricity', value: '100%' },
                        { key: 'materials', label: 'Sustainable Materials', value: '62%' },
                        { key: 'co2', label: 'CO₂ Reduction vs. 2019', value: '−49%' },
                        { key: 'wages', label: 'Fair Wage Factories', value: 'Top 95%' },
                    ],
                    documents: [
                        {
                            title: 'ESG / Annual Report 2025',
                            category: 'ESG Report',
                            url: 'https://group.hugoboss.com/en/sustainability',
                        },
                        {
                            title: 'Code of Conduct',
                            category: 'Governance',
                            url: 'https://group.hugoboss.com/en/sustainability/governance',
                        },
                        {
                            title: 'Supplier Code of Conduct',
                            category: 'Supply Chain',
                            url: 'https://group.hugoboss.com/en/sustainability/partners',
                        },
                    ],
                    documentsUrl: 'https://group.hugoboss.com/en/sustainability',
                    certifications: [
                        {
                            name: 'EcoVadis',
                            badge: 'Gold Medal',
                            badgeColor: '#E8A020',
                            description: 'Sustainability rating platform · 2023',
                        },
                        {
                            name: 'Fair Wear Foundation',
                            badge: 'Member',
                            badgeColor: '#414FBD',
                            description: 'Fair labour standards · 2024',
                        },
                    ],
                },
                disclaimer: 'Information and recommendations provided within this application are generated automatically by Artificial Intelligence (AI). While we utilize advanced models, outputs may occasionally be inaccurate, incomplete, or out of date. Please independently verify critical information before use.',
            };
        }

        const response = await axios.get(`${API_BASE_URL}/supplier/${supplierId}/overview/esg-rating`, {
            params,
            headers: {
                Authorization: `Bearer ${localStorage.getItem('LOCAL_STORAGE_KEY_ACCESS_TOKEN')}`,
            },
        });

        return response.data?.data || {};
    }
}
